// Resources
import React, { createRef } from "react";

// Styles
import "draft-js/dist/Draft.css";
import "@draft-js-plugins/image/lib/plugin.css";
import "@draft-js-plugins/static-toolbar/lib/plugin.css";

// Components
import Editor from "@draft-js-plugins/editor";
import {
	ContentState,
	EditorState,
	RichUtils,
	getDefaultKeyBinding,
	convertFromRaw,
	convertToRaw,
	AtomicBlockUtils
} from "draft-js";
import { ItalicButton, BoldButton, UnderlineButton, CodeButton } from "@draft-js-plugins/buttons";
import createImagePlugin from "@draft-js-plugins/image";
import createLinkifyPlugin from "@draft-js-plugins/linkify";
import createToolbarPlugin from "@draft-js-plugins/static-toolbar";

let test_json = (obj) => {
	try {
		let a = JSON.parse(obj); // eslint-disable-line
		return true;
	} catch (e) {
		return false;
	}
};
let styleMap = {
	CODE: {
		backgroundColor: "rgba(0, 0, 0, 0.05)",
		fontFamily: 'Consolas, monospace !important',
		fontSize: 12,
		padding: 2
	}
};
function getBlockStyle(block) {
	switch (block.getType()) {
		case "blockquote":
			return "RichEditor-blockquote";
		default:
			return null;
	}
}
class DraftWrapper extends React.Component {
	constructor(props) {
		super(props);
		if (props.content) {
			this.state = {
				isFocused: false,
				editorState:
					props.content.indexOf('{"blocks"') !== 0 || !test_json(props.content)
						? EditorState.createWithContent(ContentState.createFromText(props.content))
						: EditorState.createWithContent(convertFromRaw(JSON.parse(props.content)))
			};
		} else {
			this.state = { editorState: EditorState.createEmpty() };
		}
		this.editorRef = createRef();

		this.focus = () => {
			this.editorRef.current.focus();
			this.setState({ isFocused: true });
		};
		this.blur = () => {
			this.setState({ isFocused: false });
		};
		this.onChange = (editorState) => {
			this.setState({ editorState });
			let storage = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
			if (this.props.reportDraftChange) this.props.reportDraftChange(storage);
		};

		this.handleKeyCommand = this._handleKeyCommand.bind(this);
		this.mapKeyToEditorCommand = this._mapKeyToEditorCommand.bind(this);
		this.toggleBlockType = this._toggleBlockType.bind(this);
		this.toggleInlineStyle = this._toggleInlineStyle.bind(this);

		// Create separate plugin instances for each editor instance
		this.imagePlugin = createImagePlugin();
		// this.inlineToolbarPlugin = createInlineToolbarPlugin({
		// 	structure: [BoldButton, ItalicButton, UnderlineButton]
		// });
		this.linkifyPlugin = createLinkifyPlugin({
			target: "_blank",
			rel: "noreferrer",
			component: (props) => <a {...props} title="CMD/CTRL + click to open" /> // eslint-disable-line
		});
		this.toolbarPlugin = createToolbarPlugin();
		// this.sideToolbarPlugin = createSideToolbarPlugin({
		// 	position: "right",
		// 	popperOptionsThis: {
		// 		placement: 'right',
		// 	  }
		// });

		this.plugins = [this.linkifyPlugin, this.imagePlugin, this.toolbarPlugin];
		this.editorRef = createRef();
	}

	_handleKeyCommand(command, editorState) {
		const newState = RichUtils.handleKeyCommand(editorState, command);
		if (newState) {
			this.onChange(newState);
			return true;
		}
		return false;
	}

	_mapKeyToEditorCommand(e) {
		if (e.keyCode === 9 /* TAB */) {
			const newEditorState = RichUtils.onTab(e, this.state.editorState, 4 /* maxDepth */);
			if (newEditorState !== this.state.editorState) {
				this.onChange(newEditorState);
			}
			return;
		}
		return getDefaultKeyBinding(e);
	}

	_toggleBlockType(blockType) {
		this.onChange(RichUtils.toggleBlockType(this.state.editorState, blockType));
	}

	_toggleInlineStyle(inlineStyle) {
		this.onChange(RichUtils.toggleInlineStyle(this.state.editorState, inlineStyle));
	}

	convertToPlainText = () => {
		const contentState = this.state.editorState.getCurrentContent();
		const rawContentState = convertToRaw(contentState);
		let plainText = "";
		rawContentState.blocks.forEach((block) => {
			let text = block.text;
			switch (block.type) {
				case "header-one":
					text = `<h1 style="font-size: 1rem;font-weight:normal;"><a href="/wiki/${
						this.props.item.slug
					}#goto_${this.toSlug(text.replace(/<\/?mark>/g, ""))}" style="color:inherit;">${text}</a></h1>`;
					break;
				case "header-two":
					text = `<h2 style="font-size: 1rem;font-weight:normal;"><a href="/wiki/${
						this.props.item.slug
					}#goto_${this.toSlug(text.replace(/<\/?mark>/g, ""))}" style="color:inherit;">${text}</a></h2>`;
					break;
				case "header-three":
					text = `<h3 style="font-size: 1rem;font-weight:normal;"><a href="/wiki/${
						this.props.item.slug
					}#goto_${this.toSlug(text.replace(/<\/?mark>/g, ""))}" style="color:inherit;">${text}</a></h3>`;
					break;
				default:
					text = `<p>${text}</p>`;
			}
			plainText += text;
		});
		return plainText;
	};

	toSlug = (str) =>
		str
			.toLowerCase()
			.trim()
			.replace(/\s+/g, "_")
			.replace(/[^a-z0-9_]/gi, "");

	processSearchQuery(search) {
		const operators = ["AND", "OR", "NOT"];
		const stack = [];
		let currentTerm = "";
		let inQuotes = false;

		for (let i = 0; i < search.length; i++) {
			const char = search[i];

			if (char === '"') {
				inQuotes = !inQuotes;
				currentTerm += char;
			} else if (inQuotes) {
				currentTerm += char;
			} else if (char === " " && operators.includes(currentTerm.toUpperCase())) {
				stack.push({ type: currentTerm.toUpperCase(), value: null });
				currentTerm = "";
			} else if (char === "(" || char === ")") {
				if (currentTerm.length > 0) {
					stack.push({ type: "TERM", value: currentTerm.trim() });
					currentTerm = "";
				}
				stack.push({ type: char, value: null });
			} else if (char === " " && currentTerm.length > 0) {
				stack.push({ type: "TERM", value: currentTerm.trim() });
				currentTerm = "";
			} else if (char === " ") {
				continue;
			} else {
				currentTerm += char;
			}
		}

		if (currentTerm.length > 0) {
			stack.push({ type: "TERM", value: currentTerm.trim() });
		}

		// Check for mismatched quotes
		if (inQuotes) {
			throw new Error("Mismatched quotes in search term");
		}

		// Combine consecutive terms not separated by operators into single terms
		const combinedStack = [];
		let buffer = "";

		for (let i = 0; i < stack.length; i++) {
			const token = stack[i];

			if (token.type === "TERM") {
				if (buffer.length > 0) {
					buffer += " " + token.value;
				} else {
					buffer = token.value;
				}
			} else {
				if (buffer.length > 0) {
					combinedStack.push({ type: "TERM", value: buffer });
					buffer = "";
				}
				combinedStack.push(token);
			}
		}

		if (buffer.length > 0) {
			combinedStack.push({ type: "TERM", value: buffer });
		}

		return combinedStack;
	}
	imageIcon = () => (
		<svg width="20px" height="20px" viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M14.2639 15.9375L12.5958 14.2834C11.7909 13.4851 11.3884 13.086 10.9266 12.9401C10.5204 12.8118 10.0838 12.8165 9.68048 12.9536C9.22188 13.1095 8.82814 13.5172 8.04068 14.3326L4.04409 18.2801M14.2639 15.9375L14.6053 15.599C15.4112 14.7998 15.8141 14.4002 16.2765 14.2543C16.6831 14.126 17.12 14.1311 17.5236 14.2687C17.9824 14.4251 18.3761 14.8339 19.1634 15.6514L20 16.4934M14.2639 15.9375L18.275 19.9565M18.275 19.9565C17.9176 20 17.4543 20 16.8 20H7.2C6.07989 20 5.51984 20 5.09202 19.782C4.71569 19.5903 4.40973 19.2843 4.21799 18.908C4.12796 18.7313 4.07512 18.5321 4.04409 18.2801M18.275 19.9565C18.5293 19.9256 18.7301 19.8727 18.908 19.782C19.2843 19.5903 19.5903 19.2843 19.782 18.908C20 18.4802 20 17.9201 20 16.8V16.4934M4.04409 18.2801C4 17.9221 4 17.4575 4 16.8V7.2C4 6.0799 4 5.51984 4.21799 5.09202C4.40973 4.71569 4.71569 4.40973 5.09202 4.21799C5.51984 4 6.07989 4 7.2 4H16.8C17.9201 4 18.4802 4 18.908 4.21799C19.2843 4.40973 19.5903 4.71569 19.782 5.09202C20 5.51984 20 6.0799 20 7.2V16.4934M17 8.99989C17 10.1045 16.1046 10.9999 15 10.9999C13.8954 10.9999 13 10.1045 13 8.99989C13 7.89532 13.8954 6.99989 15 6.99989C16.1046 6.99989 17 7.89532 17 8.99989Z"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
	insertImageURL = (entityData) => {
		const contentState = this.state.editorState.getCurrentContent();
		const contentStateWithEntity = contentState.createEntity("IMAGE", "IMMUTABLE", entityData);
		const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
		const newEditorState = EditorState.set(this.state.editorState, {
			currentContent: contentStateWithEntity
		});
		return AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, " ");
	};
	handleClick = () => {
		let input = prompt("Enter an image URL:");
		if (!input) {
			return;
		}
		const entityData = { src: input };
		let newEditorState = this.insertImageURL(entityData);
		this.onChange(newEditorState);
	};

	constructRegex(terms) {
		function escapeRegexCharacters(str) {
			return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		}

		function termToRegex(term) {
			return term.replace(/\\\*/g, ".*").replace(/\\\?/g, ".");
		}
		const regexParts = [];
		let i = 0;

		while (i < terms.length) {
			const term = terms[i];

			if (term.type === "TERM") {
				regexParts.push(termToRegex(escapeRegexCharacters(term.value)));
			} else if (term.type === "AND") {
				regexParts.push("|");
				regexParts.push(termToRegex(escapeRegexCharacters(terms[++i].value)));
			} else if (term.type === "OR") {
				regexParts.push("|");
				regexParts.push(termToRegex(escapeRegexCharacters(terms[++i].value)));
			} else if (term.type === "NOT") {
				regexParts.push(`(?!${termToRegex(escapeRegexCharacters(terms[++i].value))})`);
			} else if (term.type === "(" || term.type === ")") {
				regexParts.push(term.type);
			}

			i++;
		}

		return regexParts.join("");
	}

	render() {
		const { Toolbar } = this.toolbarPlugin;
		const { editorState } = this.state;
		let plainText = "";
		if (this.props.plainText) {
			plainText = this.convertToPlainText(); //
			let start = plainText.toLowerCase().indexOf(this.props.search.toLowerCase());
			if (this.props.search && start !== -1) {
				let sentenceStart = plainText.lastIndexOf(".", start) + 1;
				sentenceStart = sentenceStart > 0 ? sentenceStart : 0;
				plainText = (sentenceStart !== 0 ? "..." : "") + plainText.substring(sentenceStart);
			}
			if (this.props.maxLength && plainText.length > this.props.maxLength) {
				plainText = plainText.substring(0, this.props.maxLength) + "...";
			}
			if (this.props.search) {
				try {
					const parsedTerms = this.processSearchQuery(this.props.search);
					const regexString = this.constructRegex(parsedTerms);
					const regex = new RegExp(regexString, "ig");
					// Only highlight text between tags so matches inside attributes
					// (e.g. heading hrefs) don't get <mark> injected into them.
					plainText = plainText
						.split(/(<[^>]*>)/)
						.map((part) => (part.startsWith("<") ? part : part.replace(regex, "<mark>$&</mark>")))
						.join("");
				} catch (e) {
					console.log(e);
				}
			}
			return <div dangerouslySetInnerHTML={{ __html: plainText }} />;
		}

		return (
			<div className="RichEditor-root">
				<div
					onClick={this.focus}
					// onBlur={this.blur}
					id={this.props.item && this.props.item.field_type_id}
					name={this.props.item && this.props.item.field_id}
					className="textarea"
					style={{position:"relative"}}
				>
					<Editor
						blockStyleFn={getBlockStyle}
						customStyleMap={styleMap}
						editorState={editorState}
						handleKeyCommand={this.handleKeyCommand}
						keyBindingFn={this.mapKeyToEditorCommand}
						onChange={this.onChange}
						placeholder={this.props.placeholder}
						plugins={this.plugins}
						ref={this.editorRef}
						spellCheck={true}
						readOnly={this.props.readOnly}
					/>
				</div>
				{/* {this.state.isFocused && ( */}
					<div className="draft-toolbar-wrap" style={{position:"absolute",bottom: "100%"}}>
						<Toolbar>
							{(externalProps) => (
								<div>
									<BoldButton {...externalProps} />
									<ItalicButton {...externalProps} />
									<UnderlineButton {...externalProps} />
									<CodeButton {...externalProps} />
									<button onClick={this.handleClick} style={{background: "transparent", border: "none", color: "#888"}}>
										{this.imageIcon()}
									</button>
								</div>
							)}
						</Toolbar>
					</div>
				{/* )}  */}
			</div>
		);
	}
}

export default DraftWrapper;
